import { MessageCircle, Trash2, Undo2 } from 'lucide-react-native';
import { Platform, Pressable, StyleSheet, View } from 'react-native';
import Swipeable from 'react-native-gesture-handler/ReanimatedSwipeable';

import { Avatar } from '@/components/Avatar';
import { Text } from '@/components/Text';
import { colors, fonts, fontSizes, radii, spacing } from '@/constants/theme';
import { canStillFinish, stepLineFor } from '@/lib/friendProgress';
import { initialsFor } from '@/lib/initials';
import { formatDay } from '@/lib/relativeTime';
import type { EveryoneFeedItem, FriendProgressTone } from '@/types/referral';

// Re-exported: the sheet and tests import it from here historically.
export { initialsFor };

/**
 * One friend in the Refer tab's list (mock P1b/P1e, 2026-09-13). Three lines,
 * every word the server's:
 *
 *   1. name
 *   2. what happens next, colored by tone: "Needs to add $10" / "Paid you $20"
 *   3. what they last did and the day: "Opened SoFi Sep 9", plus ", nothing
 *      since" when overdue, plus the channel
 *
 * Dates, never relative times ("1d ago" meant nothing to anyone). Every
 * friend who can still finish wears the same NUDGE pill. It used to appear
 * only on overdue rows, then briefly in two colours (amber = overdue, grey
 * = fresh); both went 2026-09-17 because nobody can decode a colour with
 * no words next to it. Overdue still tints the next-step line and the dot,
 * and the detail sheet says it in words. Paid and rejected rows have
 * nothing to nudge. The word "Confirmed" never appears.
 */
interface WhoClickedRowProps {
  item: EveryoneFeedItem;
  isLast: boolean;
  onNudge: () => void;
  onPress: () => void;
  /** Set on rows in the "Removed" view — swaps the trailing pill for UNDO. */
  onUndo?: () => void;
  /**
   * Swipe-left-to-remove, iOS Messages style. Only passed for rows that
   * are actually removable — paid rows and rows already in the Removed
   * view get no swipe, matching the detail sheet and the server's own
   * rule (it 422s on a paid referral).
   *
   * Additive, never the only path: the row tap opens the sheet, which
   * carries the same Remove action for web and for anyone who never
   * discovers the gesture.
   */
  onSwipeRemove?: () => void;
}

export function WhoClickedRow({
  item,
  isLast,
  onNudge,
  onPress,
  onUndo,
  onSwipeRemove,
}: WhoClickedRowProps) {
  const lines = rowLinesFor(item);
  const isPaid = lines.tone === 'paid';
  // The same rule as the sheet's Text button and the nudge screen's list.
  const canNudge = canStillFinish(item);
  // Sits on the name line, not beside the whole body: the name is short, so
  // the pill never squeezes the next-step or progress lines into wrapping.
  const trailing = onUndo ? (
    <Pressable
      onPress={onUndo}
      hitSlop={{ top: 10, bottom: 10, left: 8, right: 8 }}
      accessibilityRole="button"
      accessibilityLabel={`Put ${item.first_name} back on your list`}
    >
      <View style={styles.undoPill}>
        <Undo2 size={12} color={colors.deepGreen} strokeWidth={2.5} />
        <Text variant="captionBold" style={styles.undoPillText}>
          UNDO
        </Text>
      </View>
    </Pressable>
  ) : isPaid ? (
    <View style={styles.paidPill}>
      <Text variant="captionBold" style={styles.paidPillText}>
        PAID
      </Text>
    </View>
  ) : canNudge ? (
    <Pressable
      onPress={onNudge}
      hitSlop={{ top: 10, bottom: 10, left: 8, right: 8 }}
      accessibilityRole="button"
      accessibilityLabel={`Nudge ${item.first_name}`}
      testID={`who-clicked-nudge-${item.referral_id}`}
    >
      <View style={styles.nudgePill}>
        <MessageCircle size={12} color={colors.deepGreen} strokeWidth={2.5} />
        <Text variant="captionBold" style={styles.nudgePillText}>
          NUDGE
        </Text>
      </View>
    </Pressable>
  ) : null;

  const row = (
    // Row body opens the detail sheet. The trailing pill is a nested
    // Pressable, which wins the touch in RN, so tapping NUDGE never falls
    // through to the sheet.
    <Pressable
      onPress={onPress}
      // Not itself an accessibility element: with the row as one element,
      // VoiceOver could never reach the NUDGE / UNDO pill nested inside it,
      // and a removed friend's UNDO had no other way back once its
      // tombstone settled. The body below is the element; a double-tap on
      // it lands here as an ordinary press.
      accessible={false}
      style={({ pressed }) => [
        styles.row,
        !isLast && styles.rowDivider,
        item.hidden && styles.rowRemoved,
        pressed && styles.rowPressed,
      ]}
    >
      <Avatar
        initials={initialsFor(item.name)}
        size={40}
        tone={lines.tone === 'not_started' ? 'frost' : 'green'}
      />
      <View
        style={styles.rowBody}
        accessible
        accessibilityRole="button"
        accessibilityLabel={`${item.name}. ${lines.next}. ${lines.did}. Open details.`}
      >
        <View style={styles.nameRow}>
          <Text style={styles.name} numberOfLines={1}>
            {item.name}
          </Text>
          {trailing}
        </View>
        <Text
          style={[styles.next, TONE_TEXT[lines.tone]]}
          numberOfLines={2}
          testID={`who-clicked-next-${item.referral_id}`}
        >
          {lines.next}
        </Text>
        <View style={styles.didRow}>
          {lines.stepCount > 0 ? (
            <StepDots done={lines.stepIndex} total={lines.stepCount} tone={lines.tone} />
          ) : null}
          <Text style={styles.did} numberOfLines={2} testID={`who-clicked-did-${item.referral_id}`}>
            {lines.did}
          </Text>
        </View>
      </View>
    </Pressable>
  );

  // Web gets no swipe wrapper at all. `overflow: hidden` on the card only
  // clips the action pane's PIXELS — the Pressable stays mounted and stays in
  // the browser's focus order, so a keyboard user could Tab to an invisible
  // "Remove" button and fire it. Swipe is an iOS affordance anyway; the row
  // tap into the sheet is the web path.
  if (!onSwipeRemove || Platform.OS === 'web') return row;

  return (
    <Swipeable
      renderRightActions={(_progress, _translation, methods) => (
        <SwipeRemoveAction
          firstName={item.first_name || item.name}
          onPress={() => {
            // Close before acting: the row is about to become a tombstone,
            // and leaving the pane open would carry the open swipe state
            // onto a row that no longer has a swipe action.
            methods.close();
            onSwipeRemove();
          }}
        />
      )}
      // Snap-back felt sluggish on device with the defaults. friction=2
      // makes the row track the finger more tightly, and a short spring
      // (rather than the default long one) makes the release land instead
      // of drifting back.
      friction={2}
      rightThreshold={40}
      overshootRight={false}
      animationOptions={{ duration: 180, dampingRatio: 1 }}
    >
      {row}
    </Swipeable>
  );
}

/**
 * The step ring in miniature: one dot per tappable step, filled through the
 * furthest confirmed one. Leads line 3 so "●○ Opened SoFi Sep 11" reads as
 * step one of two, without a word.
 */
function StepDots({
  done,
  total,
  tone,
}: {
  done: number;
  total: number;
  tone: FriendProgressTone;
}) {
  const fill =
    tone === 'paid' ? styles.dotPaid : tone === 'overdue' ? styles.dotOverdue : styles.dotDone;
  return (
    <View
      style={styles.dots}
      accessibilityElementsHidden
      importantForAccessibility="no-hide-descendants"
    >
      {Array.from({ length: total }, (_, i) => (
        <View key={i} style={[styles.dot, i < done ? fill : styles.dotTodo]} />
      ))}
    </View>
  );
}

interface SwipeRemoveActionProps {
  firstName: string;
  onPress: () => void;
}

/**
 * The red pane revealed by swiping a row left. Deliberately reads "Remove",
 * not "Delete" — nothing is destroyed, the referral keeps earning, and the
 * word people associate with permanence would misdescribe it.
 */
function SwipeRemoveAction({ firstName, onPress }: SwipeRemoveActionProps) {
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={`Remove ${firstName} from your list`}
      testID="who-clicked-swipe-remove"
      style={({ pressed }) => [styles.swipeAction, pressed && styles.swipeActionPressed]}
    >
      <Trash2 size={18} color={colors.frost} strokeWidth={2.25} />
      <Text variant="captionBold" style={styles.swipeActionText}>
        Remove
      </Text>
    </Pressable>
  );
}

interface TombstoneRowProps {
  item: EveryoneFeedItem;
  isLast: boolean;
  onUndo: () => void;
}

/**
 * Stands in for a row that was just removed, at its original position and
 * height, so the list doesn't jump and the result of the tap stays visible.
 * A toast can't do this job — the Refer tab's toast is `pointerEvents:
 * none` and auto-dismisses, so nothing inside it can be tapped.
 */
export function TombstoneRow({ item, isLast, onUndo }: TombstoneRowProps) {
  return (
    <View style={[styles.row, styles.tombstone, !isLast && styles.rowDivider]}>
      <View style={styles.rowBody}>
        <Text variant="body" color="textMuted" numberOfLines={1}>
          {`${item.first_name || item.name} removed`}
        </Text>
      </View>
      <Pressable
        onPress={onUndo}
        hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
        accessibilityRole="button"
        accessibilityLabel={`Undo removing ${item.first_name || item.name}`}
        testID={`who-clicked-undo-${item.referral_id}`}
      >
        <View style={styles.undoPill}>
          <Undo2 size={12} color={colors.deepGreen} strokeWidth={2.5} />
          <Text variant="captionBold" style={styles.undoPillText}>
            UNDO
          </Text>
        </View>
      </Pressable>
    </View>
  );
}

export interface RowLines {
  tone: FriendProgressTone;
  /** Line 2. */
  next: string;
  /** Line 3: what they last did and the day. */
  did: string;
  stepIndex: number;
  stepCount: number;
}

/**
 * The row's words. Server-authored via `progress`; against an older server
 * (no `progress`) the row says only what it can vouch for: the stage and
 * the signup day.
 */
export function rowLinesFor(item: EveryoneFeedItem, now = Date.now()): RowLines {
  // The link they came through (`source_label`) stays off the row: "· Share"
  // meant nothing to anyone. The sheet shows it as a chip next to the name.
  const progress = item.progress;
  if (!progress) {
    const paid = item.stage === 'paid';
    const signedUp = item.signed_up_at
      ? `Signed up ${formatDay(item.signed_up_at, now)}`
      : 'Signed up';
    return {
      tone: paid ? 'paid' : 'active',
      next: paid ? 'Paid you' : 'In progress',
      did: signedUp,
      stepIndex: 0,
      stepCount: 0,
    };
  }
  // Overdue is carried by the amber line 2; ", nothing since" on line 3
  // wrapped to an orphaned word at 393pt. The sheet says it.
  return {
    tone: progress.tone,
    next: progress.next_label,
    did: stepLineFor(progress, now),
    stepIndex: progress.step_index,
    stepCount: progress.step_count,
  };
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    paddingVertical: spacing.base,
    // The list is full-bleed (see WhoClickedCard.list) so rows and the swipe
    // pane reach the card edge; content keeps the card's inset here instead.
    paddingHorizontal: spacing.lg,
    backgroundColor: colors.surface,
  },
  rowDivider: {
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.border,
  },
  rowPressed: {
    opacity: 0.6,
  },
  rowRemoved: {
    opacity: 0.55,
  },
  tombstone: {
    paddingVertical: spacing.md,
  },
  rowBody: {
    flex: 1,
    minWidth: 0,
    gap: 5,
  },
  nameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
  },
  name: {
    flexShrink: 1,
    fontFamily: fonts.bodyBold,
    fontSize: fontSizes.lg,
    lineHeight: 22,
    color: colors.deepGreen,
  },
  next: {
    fontFamily: fonts.bodySemiBold,
    fontSize: fontSizes.base,
    lineHeight: 20,
  },
  nextPaid: { color: colors.accentText },
  nextActive: { color: colors.deepGreen },
  nextOverdue: { color: colors.warnPillText },
  // The deal stepper's overdue/failed red, the same the sheet's card uses.
  nextFailed: { color: colors.overduePillText },
  nextNotStarted: { color: colors.textMuted },
  didRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.sm,
  },
  did: {
    flexShrink: 1,
    fontFamily: fonts.body,
    fontSize: fontSizes.sm,
    lineHeight: 18,
    color: colors.textMuted,
  },
  dots: {
    flexDirection: 'row',
    gap: 4,
    // Centered on line 3's 18pt line box.
    marginTop: 5,
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  dotDone: { backgroundColor: colors.deepGreen },
  dotPaid: { backgroundColor: colors.electricGreen },
  dotOverdue: { backgroundColor: colors.warning },
  dotTodo: { backgroundColor: colors.track },
  nudgePill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: radii.full,
    // One colour for every row. Deep Green on a neutral plate reads as an
    // action (the UNDO pill uses the same ink), not as disabled — grey text
    // here looked switched off next to Cara's amber.
    backgroundColor: colors.neutralPillBg,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: colors.border,
  },
  nudgePillText: {
    color: colors.deepGreen,
    fontSize: fontSizes.xxs,
    letterSpacing: 0.5,
  },
  swipeAction: {
    width: 88,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    backgroundColor: colors.danger,
  },
  swipeActionPressed: {
    opacity: 0.8,
  },
  swipeActionText: {
    color: colors.frost,
    fontSize: fontSizes.xs,
  },
  undoPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: radii.full,
    backgroundColor: colors.surface,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: colors.border,
  },
  undoPillText: {
    color: colors.deepGreen,
    fontSize: fontSizes.xxs,
    letterSpacing: 0.5,
  },
  paidPill: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: radii.full,
    backgroundColor: colors.paidPillBg,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: colors.paidPillBorder,
  },
  paidPillText: {
    color: colors.paidPillText,
    fontSize: fontSizes.xxs,
    letterSpacing: 0.5,
  },
});

// Declared after `styles` — references it at module load. Kept in step with
// STATUS_STYLES in ReferralDetailSheet.tsx (the sheet's card per tone).
const TONE_TEXT: Record<FriendProgressTone, { color: string }> = {
  paid: styles.nextPaid,
  active: styles.nextActive,
  overdue: styles.nextOverdue,
  failed: styles.nextFailed,
  not_started: styles.nextNotStarted,
};
